import {
  InvoiceAttachment,
  InvoiceLevelPaymentModes,
  InvoiceLineItem,
  InvoiceSurchargeState,
  InvoicesState
} from 'containers/invoices/InvoicesSlice';
import {
  AdditionalTax,
  Attachment,
  IInvoiceAttachmentRequest,
  IInvoiceServiceCharge,
  IInvoiceServiceChargeFromResponse,
  IInvoiceServiceChargeRequest,
  IInvoiceServiceChargeTaxRequest,
  ILineItemRequest,
  ILineItemTaxRequest,
  IUniqueTaxRequest,
  LineItemTaxFromAPIResponse,
  ServiceChargeV2
} from 'containers/invoices/invoices.model';
import {
  formatDecimalThreePlaces,
  getAmountWithCurrency,
  getPercentageAmount,
  reverseCalculatePercentage,
  roundToTwoDecimals
} from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import {
  InvoiceAttachmentEditPage,
  InvoiceLineItemEditPage,
  InvoiceSurchargeStateEditPage
} from './edit-invoice/invoicesEditSlice';
import { DiscountFormData, LineItemAdditionalTax, initialDiscountFormData } from './types';
import { DISPUTE_STATUS } from 'containers/disputes/constants';
import { constructArrayObjectAsString } from 'utils/commonUtils';
import { IServiceCharge } from 'containers/product-catalog/service-charges/serviceCharge.model';
import { initServiceCharge } from './create-invoice/constants';
import { AbstractITax, ITax } from 'containers/product-catalog/taxes/taxes.model';
import { GlobalServiceChargeTax } from 'containers/product-catalog/catalog.model';
import { FeatureToggleState } from 'containers/settings/constants';
import { EssentialInvoiceData, InvoiceStatus, StatusDisplayData } from 'containers/invoices/invoices.model';
import isEqual from 'lodash/isEqual';
import { AddressField, Customer } from './invoices.model';
import { InvoiceErrorFields } from './create-invoice/types';
import { PaymentMode } from 'containers/settings/settings.model';
import { PaymentCategoryTypes } from 'containers/settings/type';
import { store } from 'store/store';
import { PaymentMethodsForInvoiceFilter } from 'containers/home/components/constants';
import { DiscountV2, LineItemV2 } from './invoices.model';
import { ReminderConfig } from 'components/reminder-modal/types';
import { calculateScheduledDate } from 'containers/estimates/utils';
import { InvoiceV2QueryParams } from './hooks/usePaginatedGetInvoicesV2Query';
import { filterEmptyParams } from 'utils/apiUtils';
import { DISPUTE_REFUND_STATUS_INDICATOR, StatusIndicator } from './constants';

export const parseLineItemTaxesFromResponse = (taxes: LineItemTaxFromAPIResponse[]): LineItemAdditionalTax[] => {
  if (taxes) {
    const lineItemLevelTaxes: LineItemAdditionalTax[] = taxes.map((tax, index) => {
      return {
        id: tax?.catalogueTaxId || index,
        name: tax?.name,
        percentage: tax?.percentage,
        amount: +tax?.amount
      };
    });
    return lineItemLevelTaxes;
  }
  return [];
};

export const prepareInvoiceServiceCharge = ({
  serviceCharge = {
    id: 0,
    name: '',
    amount: '',
    percentage: '',
    taxes: []
  }
}: {
  serviceCharge: IInvoiceServiceChargeFromResponse;
}): IInvoiceServiceCharge => {
  const invoiceServiceCharge: IInvoiceServiceCharge = {
    id: serviceCharge?.id,
    name: serviceCharge?.name,
    amount: serviceCharge?.amount,
    percentage: serviceCharge?.percentage,
    taxes:
      serviceCharge?.taxes?.map(tax => ({
        id: tax?.catalogueTaxId,
        name: tax?.name,
        percentage: tax?.percentage
      })) ?? []
  };
  return invoiceServiceCharge;
};

export const getAttachmentArray = (selectedInvoiceAttachments: InvoiceAttachment[]) => {
  const attachmentArray: IInvoiceAttachmentRequest[] = selectedInvoiceAttachments.map(attachment => {
    return {
      title: attachment.title,
      url: attachment.url,
      description: attachment.description,
      source: attachment.source || 'INVOICE'
    };
  });
  return attachmentArray;
};

const prepareLineItemLevelTaxArray = (lineItemTaxes: LineItemAdditionalTax[]) => {
  if (lineItemTaxes?.length > 0) {
    const taxes = lineItemTaxes.map(tax => {
      return {
        name: tax.name,
        percentage: tax.percentage,
        amount: tax.amount.toString(),
        catalogueTaxId: tax.id
      } as ILineItemTaxRequest;
    });

    return { taxes: taxes };
  }
  return {};
};

export const getLineItemsArray = (selectedInvoice: InvoicesState) => {
  const lineItemRequestArray: ILineItemRequest[] = selectedInvoice?.invoiceLineItems?.map(lineItem => {
    return {
      catalogueLineItemId: lineItem.catalogueLineItemId,
      name: lineItem.itemDescription,
      amount: lineItem.total.toString(),
      qty: lineItem.quantity,
      unitPrice: lineItem.unitCost,
      unitCost: lineItem.originalUnitCost,
      markupPercentage: lineItem.markupPercentage ?? '0',
      additionalDetails: lineItem.additionalDetails,
      ...prepareLineItemLevelTaxArray(lineItem?.taxes)
    };
  });

  return lineItemRequestArray;
};

export const getFormattedAdditionalTaxPercent = (additionalTaxPercent: string) => {
  return formatDecimalThreePlaces(+additionalTaxPercent);
};

export const getServiceChargeEntityValue = (serviceCharge: IServiceCharge | IInvoiceServiceCharge): string => {
  if (+serviceCharge.percentage > 0) {
    return `${formatDecimalThreePlaces(serviceCharge.percentage)}%`;
  } else if (+serviceCharge.amount > 0) {
    return getAmountWithCurrency(serviceCharge.amount);
  }
  return '_';
};

export const doesServiceChargeNameContainTax = (serviceCharge: IInvoiceServiceCharge | null | undefined): boolean => {
  return (serviceCharge?.name ?? '').toLowerCase().includes('tax');
};

export const calculateLineItemAdditionalTaxInCurrency = (additionalTaxPercent: string, totalLineItemCost: number) => {
  return roundToTwoDecimals((+additionalTaxPercent * +totalLineItemCost) / 100);
};

export const getAggregatedTaxInCurrencyWithSymbol = (
  lineItems: any[],
  additionalTax: LineItemAdditionalTax,
  selectedServiceCharge?: IInvoiceServiceCharge
): string => {
  let aggregatedTaxSum = 0;
  const selectedServiceChargeTaxes = selectedServiceCharge?.taxes ?? [];
  aggregatedTaxSum = lineItems.reduce((taxSum, currentLineItem) => {
    const additionaltaxInCurrentItem = currentLineItem?.taxes?.find(tax => tax.id === additionalTax.id);
    if (additionaltaxInCurrentItem) {
      return taxSum + (additionaltaxInCurrentItem?.amount ?? 0);
    }
    return taxSum;
  }, 0);

  const isTaxInServiceCharge = selectedServiceChargeTaxes.find(tax => tax.id === additionalTax.id);
  if (isTaxInServiceCharge) {
    const serviceChargeTaxAmount = getPercentageAmount(
      selectedServiceCharge?.totalAmount ?? 0,
      isTaxInServiceCharge.percentage
    );
    aggregatedTaxSum = aggregatedTaxSum + serviceChargeTaxAmount;
  }

  return getAmountWithCurrency(aggregatedTaxSum.toString());
};

export const getAggregatedTaxInCurrency = (
  lineItems: any[],
  additionalTax: LineItemAdditionalTax,
  selectedServiceCharge?: IInvoiceServiceCharge
): string => {
  let aggregatedTaxSum = 0;
  const selectedServiceChargeTaxes = selectedServiceCharge?.taxes ?? [];
  aggregatedTaxSum = lineItems.reduce((taxSum, currentLineItem) => {
    const additionaltaxInCurrentItem = currentLineItem?.taxes?.find(tax => tax.id === additionalTax.id);
    if (additionaltaxInCurrentItem) {
      return taxSum + (additionaltaxInCurrentItem.amount ?? 0);
    }
    return taxSum;
  }, 0);

  const isTaxInServiceCharge = selectedServiceChargeTaxes.find(tax => tax.id === additionalTax.id);
  if (isTaxInServiceCharge) {
    const serviceChargeTaxAmount = getPercentageAmount(
      selectedServiceCharge?.totalAmount ?? 0,
      isTaxInServiceCharge.percentage
    );
    aggregatedTaxSum = aggregatedTaxSum + serviceChargeTaxAmount;
  }
  return roundToTwoDecimals(aggregatedTaxSum).toString();
};

export const getDisputeStyles = (status: DISPUTE_STATUS) => {
  switch (status) {
    case DISPUTE_STATUS.LOST:
      return { backgroundColor: '#FF7A7A' };
    case DISPUTE_STATUS.WON:
      return { backgroundColor: '#27AE60' };
    case DISPUTE_STATUS.NEEDS_ATTENTION:
    case DISPUTE_STATUS.NEEDS_RESPONSE:
      return { backgroundColor: '#F2994A' };
    case DISPUTE_STATUS.PAST_DUE:
      return { backgroundColor: '#F2B85C', color: '#333333' };
    case DISPUTE_STATUS.EVIDENCE_SUBMITTED:
      return { backgroundColor: '#A4D7FA', color: '#333333' };
    default:
      return { backgroundColor: '#5783BD' };
  }
};

export const getLineItemsArrayForEvent = (lineItems: InvoiceLineItem[] | InvoiceLineItemEditPage[]) => {
  const lineItemRequestArray: ILineItemRequest[] = lineItems.map(lineItem => {
    return {
      catalogueLineItemId: lineItem.catalogueLineItemId,
      name: lineItem.itemDescription,
      amount: lineItem.total.toString(),
      qty: lineItem.quantity,
      unitPrice: lineItem.unitCost,
      unitCost: lineItem.originalUnitCost,
      markupPercentage: lineItem.markupPercentage ?? '0',
      additionalDetails: lineItem.additionalDetails,
      lineItemTax: constructArrayObjectAsString(prepareLineItemLevelTaxArray(lineItem?.taxes)?.taxes, 'line item tax')
    };
  });
  return lineItemRequestArray;
};

export const getServiceChargeEventPayload = (serviceChargeList: IInvoiceServiceChargeRequest[] | ServiceChargeV2[]) => {
  return serviceChargeList.map(serviceCharge => ({
    ...serviceCharge,
    taxes: constructArrayObjectAsString(serviceCharge.taxes, 'taxes')
  }));
};

export const calculateServiceChargeBaseAmount = ({
  subtotal,
  serviceCharge
}: {
  subtotal: number;
  serviceCharge: IInvoiceServiceCharge;
}): number => {
  let serviceChargeBaseAmount = 0;
  if (+serviceCharge.percentage > 0) {
    serviceChargeBaseAmount = getPercentageAmount(subtotal, serviceCharge.percentage);
  } else if (+serviceCharge.amount > 0) {
    serviceChargeBaseAmount = +serviceCharge.amount;
  }
  return roundToTwoDecimals(serviceChargeBaseAmount);
};

export const calculateServiceChargeTotal = ({
  subtotal,
  serviceCharge
}: {
  subtotal: number;
  serviceCharge: IInvoiceServiceCharge;
}): number => {
  let serviceChargeBaseAmount = 0;
  let serviceChargeTaxesAmount = 0;
  let serviceChargeTotalAmount = 0;
  if (+serviceCharge.percentage > 0) {
    serviceChargeBaseAmount = getPercentageAmount(subtotal, serviceCharge.percentage);
    serviceChargeTaxesAmount = serviceCharge.taxes.reduce((sumOfServiceChargeTaxesAmount, taxOnServiceCharge) => {
      const serviceChargeTaxAmount = getPercentageAmount(serviceChargeBaseAmount, taxOnServiceCharge.percentage);
      return sumOfServiceChargeTaxesAmount + roundToTwoDecimals(serviceChargeTaxAmount);
    }, 0);
    serviceChargeTotalAmount = serviceChargeBaseAmount + serviceChargeTaxesAmount;
  } else if (+serviceCharge.amount > 0) {
    serviceChargeBaseAmount = +serviceCharge.amount;
    serviceChargeTaxesAmount = serviceCharge.taxes.reduce((sumOfServiceChargeTaxesAmount, taxOnServiceCharge) => {
      const serviceChargeTaxAmount = getPercentageAmount(serviceChargeBaseAmount, taxOnServiceCharge.percentage);
      return sumOfServiceChargeTaxesAmount + roundToTwoDecimals(serviceChargeTaxAmount);
    }, 0);
    serviceChargeTotalAmount = serviceChargeBaseAmount + serviceChargeTaxesAmount;
  }
  return roundToTwoDecimals(serviceChargeTotalAmount);
};

export const calculateInvoiceFinalAmount = ({
  subtotal,
  surcharge = 0,
  // TODO change key to service charge baseAmount
  totalServiceChargeInCurrency = 0,
  summatedLineItemLevelTaxesCurrency = 0
}: {
  subtotal: number;
  surcharge: number;
  totalServiceChargeInCurrency: number;
  summatedLineItemLevelTaxesCurrency?: number;
}) => {
  const final = subtotal + surcharge + totalServiceChargeInCurrency + summatedLineItemLevelTaxesCurrency;
  return roundToTwoDecimals(final);
};

// TODO change fn name as it also includes the taxes in service charge
export const calculateSummatedLineItemLevelTaxesCurrency = ({
  lineItems,
  selectedServiceCharge = initServiceCharge
}: {
  lineItems: InvoiceLineItem[];
  selectedServiceCharge?: IInvoiceServiceCharge;
}): number => {
  let summatedServiceChargeTaxesCurrency = 0;
  const summatedLineItemLevelTaxesCurrency = lineItems.reduce((sumOfAllItemLevelTaxes, currentLineItem) => {
    const sumOfTaxesAppliedOnSingleLineItem = currentLineItem?.taxes?.reduce(
      (sumOfItemRootLevelTaxes, currentItemLevelTax) => {
        return sumOfItemRootLevelTaxes + currentItemLevelTax?.amount;
      },
      0
    );

    if (sumOfTaxesAppliedOnSingleLineItem) {
      return sumOfAllItemLevelTaxes + sumOfTaxesAppliedOnSingleLineItem;
    }
    return sumOfAllItemLevelTaxes;
  }, 0);

  if (selectedServiceCharge?.taxes?.length > 0) {
    summatedServiceChargeTaxesCurrency = selectedServiceCharge?.taxes?.reduce(
      (sumOfAllServiceChargeTaxes, currentTax) => {
        const serviceChargeTaxAmount = getPercentageAmount(selectedServiceCharge?.totalAmount, currentTax.percentage);
        return sumOfAllServiceChargeTaxes + serviceChargeTaxAmount;
      },
      0
    );
  }

  return roundToTwoDecimals(summatedLineItemLevelTaxesCurrency + summatedServiceChargeTaxesCurrency) ?? 0;
};

export const isInvalidServiceCharge = ({
  serviceCharge = initServiceCharge
}: {
  serviceCharge: IInvoiceServiceCharge;
}) => {
  if (serviceCharge === initServiceCharge) {
    return true;
  }
  const { percentage = '0', amount = '0', name = '', totalAmount = '0' } = serviceCharge;
  if (!name || (+amount <= 0 && +percentage <= 0) || +totalAmount <= 0) {
    return true;
  }
  return false;
};

export const prepareServiceChargeListRequestPayload = (
  serviceCharge: IInvoiceServiceCharge
): IInvoiceServiceChargeRequest[] => {
  if (
    serviceCharge === initServiceCharge ||
    serviceCharge === null ||
    isInvalidServiceCharge({ serviceCharge: serviceCharge })
  ) {
    return [];
  }
  let serviceChargeTaxList: IInvoiceServiceChargeTaxRequest[] = [];
  let serviceChargeBaseAmount = +serviceCharge.totalAmount;
  if (serviceCharge?.taxes?.length > 0) {
    if (+serviceCharge.percentage > 0) {
      serviceChargeBaseAmount = +serviceCharge.totalAmount;
      serviceChargeTaxList = serviceCharge?.taxes?.map(tax => ({
        catalogueTaxId: tax.id,
        name: tax.name,
        percentage: tax.percentage,
        amount: roundToTwoDecimals(getPercentageAmount(serviceChargeBaseAmount, tax.percentage)).toString()
      }));
    } else if (+serviceCharge.amount > 0) {
      serviceChargeBaseAmount = +serviceCharge.amount;
      serviceChargeTaxList = serviceCharge?.taxes?.map(tax => ({
        catalogueTaxId: tax.id,
        name: tax.name,
        percentage: tax.percentage,
        amount: roundToTwoDecimals(getPercentageAmount(serviceChargeBaseAmount, tax.percentage)).toString()
      }));
    }
  }
  return [
    {
      name: serviceCharge.name,
      amount: serviceCharge.amount,
      percentage: serviceCharge.percentage,
      totalAmount: serviceChargeBaseAmount.toString(),
      taxes: serviceChargeTaxList
    }
  ];
};

export const prepareSummatedUniqueTaxList = ({
  appliedLineItemLevelTaxes = [],
  invoiceLineItems = [],
  selectedServiceCharge = initServiceCharge
}: {
  appliedLineItemLevelTaxes: AdditionalTax[];
  invoiceLineItems: InvoiceLineItem[] | InvoiceLineItemEditPage[];
  selectedServiceCharge: IInvoiceServiceCharge;
}) => {
  if (appliedLineItemLevelTaxes.length > 0) {
    const summatedUniqueTaxes = appliedLineItemLevelTaxes.map(tax => {
      return {
        catalogueTaxId: tax?.id,
        name: tax.name,
        percentage: tax.percentage,
        amount: getAggregatedTaxInCurrency(invoiceLineItems, tax, selectedServiceCharge)
      } as IUniqueTaxRequest;
    });
    return { taxes: summatedUniqueTaxes };
  }
  return {};
};

export const prepareLineItemTaxArray = (lineItemTaxes: LineItemAdditionalTax[]): { taxes: ILineItemTaxRequest[] } => {
  if (lineItemTaxes?.length > 0) {
    const taxes = lineItemTaxes.map(tax => {
      return {
        name: tax.name,
        percentage: tax.percentage,
        amount: tax.amount.toString(),
        catalogueTaxId: tax.id
      } as ILineItemTaxRequest;
    });

    return { taxes: taxes };
  }
  return { taxes: [] };
};

export const prepareLineItemList = (invoiceLineItems: InvoiceLineItem[] | InvoiceLineItemEditPage[] = []) => {
  if (invoiceLineItems?.length > 0) {
    const lineItemRequestArray: ILineItemRequest[] = invoiceLineItems.map(lineItem => {
      return {
        ...(lineItem?.catalogueLineItemId ? { catalogueLineItemId: lineItem?.catalogueLineItemId } : {}),
        name: lineItem.itemDescription,
        amount: lineItem.total.toString(),
        qty: lineItem.quantity,
        unitPrice: lineItem.unitCost,
        unitCost: lineItem.originalUnitCost ?? '',
        markupPercentage: lineItem.markupPercentage ?? '',
        additionalDetails: lineItem.additionalDetails,
        sku: lineItem.sku ?? '',
        ...prepareLineItemTaxArray(lineItem?.taxes)
      };
    });
    return lineItemRequestArray;
  }
  return [];
};

export const isValidCatalogueLineItemId = (id: number) => {
  return id !== undefined && id !== null && id !== 0;
};

export const hasValidCatalogueServiceChargeId = (id: number) => {
  return id !== undefined && id !== null && id !== 0;
};

export const isValidSku = (sku: string) => {
  return sku !== undefined && sku !== null && sku !== '';
};

export const calculateInvoiceSubtotalAndNewDiscount = ({
  totalCostOfItems,
  discountData
}: {
  totalCostOfItems: number;
  discountData: DiscountFormData;
}): { subtotal: number; discountData: DiscountFormData } => {
  if (totalCostOfItems && isValidDiscountData(discountData)) {
    if (discountData.isDiscountAmountSelected) {
      const discountAmount = +discountData.discountAmount;
      const subtotal = roundToTwoDecimals(totalCostOfItems - +discountData.discountAmount);
      const reverseCalculatedDiscountPercent = reverseCalculatePercentage({
        value: discountAmount,
        total: totalCostOfItems
      });
      const newDiscountData: DiscountFormData = {
        discountPercent: reverseCalculatedDiscountPercent.toString(),
        discountAmount: discountData.discountAmount,
        isDiscountAmountSelected: true,
        discountInCurrency: discountData.discountAmount,
        amountAfterDiscount: subtotal.toString()
      };
      return { subtotal: subtotal, discountData: newDiscountData };
    }
    // discount is of percentage type
    const finalDiscountInCurrency = getPercentageAmount(totalCostOfItems, discountData.discountPercent, true);
    const subtotal = totalCostOfItems - finalDiscountInCurrency;
    const newDiscountData: DiscountFormData = {
      discountPercent: discountData.discountPercent,
      discountAmount: '',
      isDiscountAmountSelected: false,
      discountInCurrency: finalDiscountInCurrency.toString(),
      amountAfterDiscount: subtotal.toString()
    };
    return { subtotal, discountData: newDiscountData };
  }
  return { subtotal: totalCostOfItems ?? 0, discountData };
};

export const isValidDiscountData = (discountData: DiscountFormData) => {
  return (
    discountData !== undefined &&
    discountData !== null &&
    discountData !== initialDiscountFormData &&
    (+discountData.discountAmount > 0 || +discountData.discountPercent > 0)
  );
};

export const prepareDiscountRelatedPayload = (
  discountData: DiscountFormData
): { discount: string; discountPercentage: string; isDiscountAmountSelected: boolean } | Record<string, never> => {
  if (isValidDiscountData(discountData)) {
    return {
      discount: discountData.discountInCurrency,
      discountPercentage: discountData.discountPercent,
      isDiscountAmountSelected: discountData.isDiscountAmountSelected ?? false
    };
  }
  return {
    discount: '0',
    discountPercentage: '0',
    isDiscountAmountSelected: false
  };

  return {};
};

export const hasSameTaxes = ({
  primaryIDSet = new Set<number>(),
  secondaryIDSet = new Set<number>()
}: {
  primaryIDSet: Set<number>;
  secondaryIDSet: Set<number>;
}): boolean => {
  if (primaryIDSet.size !== secondaryIDSet.size) {
    return false;
  }
  for (const id of primaryIDSet) {
    if (!secondaryIDSet.has(id)) return false;
  }
  return true;
};

export const isSameCatalogLineItem = ({
  primaryItem,
  secondaryItem
}: {
  primaryItem: InvoiceLineItem;
  secondaryItem: InvoiceLineItem;
}) => {
  if (primaryItem?.catalogueLineItemId === secondaryItem?.catalogueLineItemId) {
    const primaryItemTaxIDSet = new Set<number>(primaryItem?.taxes?.map(tax => tax.id));
    const secondaryItemTaxIDSet = new Set<number>(secondaryItem?.taxes?.map(tax => tax.id));

    if (
      secondaryItem.catalogueLineItemId === primaryItem.catalogueLineItemId &&
      secondaryItem.itemDescription === primaryItem.itemDescription &&
      +secondaryItem.unitCost === +primaryItem.unitCost &&
      secondaryItem.additionalDetails === primaryItem.additionalDetails &&
      hasSameTaxes({ primaryIDSet: primaryItemTaxIDSet, secondaryIDSet: secondaryItemTaxIDSet }) &&
      secondaryItem.sku === primaryItem.sku
    ) {
      return true;
    }
  }
  return false;
};

export const prepareTaxListWithAllGlobalTaxes = ({
  selectedTaxList = [],
  globalTaxes = []
}: {
  selectedTaxList: AbstractITax[];
  globalTaxes: GlobalServiceChargeTax[] | ITax[];
}): number[] => {
  const deselectedGlobalTaxList = globalTaxes.filter(
    globalTax => !selectedTaxList.some(selectedTax => selectedTax.id === globalTax.id)
  );
  const taxListWithAllGlobalTaxes = [...selectedTaxList, ...deselectedGlobalTaxList];
  return taxListWithAllGlobalTaxes.map(tax => tax.id) ?? [];
};

export const getIsSignatureEnabledState = (signatureConfig: FeatureToggleState): boolean => {
  switch (signatureConfig) {
    case FeatureToggleState.ALWAYS_ON:
    case FeatureToggleState.ON_BY_DEFAULT:
      return true;
    case FeatureToggleState.OFF_BY_DEFAULT:
      return false;
    default:
      return false;
  }
};

export const getInvoiceStatusBackgroundColor = status => {
  switch (status) {
    case InvoiceStatus.PAID:
      return '#CBF4C9';
    case InvoiceStatus.PENDING:
    case InvoiceStatus.PROCESSING:
      return '#F7E2C0';
    case InvoiceStatus.REFUNDED:
    case InvoiceStatus.REFUND_INITIATED:
      return '#A4D7FA';
    case InvoiceStatus.PARTIALLY_REFUNDED:
      return '#ECF1F7';
    case InvoiceStatus.PARTIALLY_PAID:
      return '#DEF1FF';
    case InvoiceStatus.OVERDUE:
      return '#D9594533';
    case InvoiceStatus.CLOSED_AMOUNT_ADJUSTED:
    case InvoiceStatus.CLOSED_BAD_DEBT:
      return '#F2F2F2';
    default:
      return '#CBF4C9';
  }
};

export const getPendingDays = (createdAt: string) => {
  const days = DateUtils.getDifferenceInDays({ secondaryDate: new Date(createdAt) });
  if (days === 0) {
    return 'Pending today';
  }
  return `Pending ${days} day${days === 1 ? '' : 's'}`;
};

export const getInvoiceStatus = (status: InvoiceStatus) => {
  switch (status) {
    case InvoiceStatus.PENDING:
      return 'Pending';
    case InvoiceStatus.PAID:
    case InvoiceStatus.PAID_QR:
      return 'Paid';
    case InvoiceStatus.REFUNDED:
      return 'Refunded';
    case InvoiceStatus.REFUND_INITIATED:
      return 'Refund initiated';
    case InvoiceStatus.PROCESSING:
      return 'Payment processing';
    case InvoiceStatus.PARTIALLY_PAID:
      return 'Partially paid';
    case InvoiceStatus.PARTIALLY_REFUNDED:
      return 'Partially refunded';
    case InvoiceStatus.OVERDUE:
      return 'Overdue';
    case InvoiceStatus.CLOSED_AMOUNT_ADJUSTED:
      return 'Closed-Amount adjusted';
    case InvoiceStatus.CLOSED_BAD_DEBT:
      return 'Closed-Bad debt';
    default:
      return `Payment ${status}`;
  }
};

export const getInvoiceStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case InvoiceStatus.PENDING:
    case InvoiceStatus.PROCESSING:
      return '#F2994A';
    case InvoiceStatus.PAID:
    case InvoiceStatus.PAID_QR:
      return '#419364';
    case InvoiceStatus.REFUNDED:
    case InvoiceStatus.REFUND_INITIATED:
    case InvoiceStatus.PARTIALLY_REFUNDED:
      return '#3E556D';
    case InvoiceStatus.PARTIALLY_PAID:
      return '#4A8DF2';
    case InvoiceStatus.OVERDUE:
      return '#D95945';
    case InvoiceStatus.CLOSED_AMOUNT_ADJUSTED:
    case InvoiceStatus.CLOSED_BAD_DEBT:
      return '#828282';
    default:
      return '#424242';
  }
};

// Helper function to create status badge using existing utilities
const createStatusBadge = (status: string) => ({
  text: getInvoiceStatus(status as InvoiceStatus),
  backgroundColor: getInvoiceStatusBackgroundColor(status),
  textColor: getInvoiceStatusColor(status as InvoiceStatus)
});

// Status display data handlers
export const getStatusDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  switch (data.status) {
    case InvoiceStatus.CLOSED_AMOUNT_ADJUSTED:
    case InvoiceStatus.CLOSED_BAD_DEBT:
      return getClosedDisplayData(data);
    case InvoiceStatus.OVERDUE:
      return getOverdueDisplayData(data);
    case InvoiceStatus.PARTIALLY_PAID:
      return getPartiallyPaidDisplayData(data);
    case InvoiceStatus.PARTIALLY_REFUNDED:
      return getPartiallyRefundedDisplayData(data);
    case InvoiceStatus.REFUNDED:
      return getRefundedDisplayData(data);
    case InvoiceStatus.PAID:
    case InvoiceStatus.PAID_QR:
    case InvoiceStatus.PENDING:
    case InvoiceStatus.PROCESSING:
    case InvoiceStatus.REFUND_INITIATED:
    default:
      return getDefaultDisplayData(data);
  }
};

const getClosedDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  if (data.totalPaidAmount > 0) {
    return {
      primaryAmount: { label: 'Total paid', value: data.totalPaidAmount },
      breakdown: [
        { label: 'Invoice total', value: data.totalAmount },
        {
          label: 'Amount adjusted',
          value: data.closureAmount
        }
      ],
      statusBadge: createStatusBadge(data.status)
    };
  }
  return {
    primaryAmount: { label: 'Invoice total', value: data.totalAmount },
    breakdown: null,
    statusBadge: createStatusBadge(data.status)
  };
};

const getOverdueDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  const hasPartialPayment = data.totalAmount !== data.pendingAmount;

  if (hasPartialPayment) {
    return {
      primaryAmount: { label: 'Amount remaining', value: data.pendingAmount },
      breakdown: [
        { label: 'Invoice total', value: data.totalAmount },
        { label: 'Amount paid', value: data.totalInvoicedAmount, tipAmount: data.tipAmount }
      ],
      statusBadge: createStatusBadge(data.status)
    };
  }

  return {
    primaryAmount: { label: 'Invoice total', value: data.totalAmount },
    breakdown: null,
    statusBadge: createStatusBadge(data.status)
  };
};

const getPartiallyPaidDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  return {
    primaryAmount: { label: 'Amount remaining', value: data.pendingAmount },
    breakdown: [
      { label: 'Invoice total', value: data.totalAmount },
      { label: 'Amount paid', value: data.totalInvoicedAmount, tipAmount: data.tipAmount }
    ],
    statusBadge: createStatusBadge(data.status)
  };
};

const getPartiallyRefundedDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  if (data.statusIndicator === StatusIndicator.FULLY_PAID) {
    return {
      primaryAmount: { label: 'Amount refunded', value: data.totalRefundedAmount },
      breakdown: [
        { label: 'Invoice total', value: data.totalAmount },
        { label: 'Amount paid after refund', value: data.totalPaidAfterRefund }
      ],
      statusBadge: createStatusBadge(data.status)
    };
  }

  return {
    primaryAmount: { label: 'Amount remaining', value: data.pendingAmount },
    breakdown: [
      { label: 'Invoice total', value: data.totalAmount },
      { label: 'Amount refunded', value: data.totalRefundedAmount }
    ],
    statusBadge: createStatusBadge(data.status)
  };
};

const getRefundedDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  return {
    primaryAmount: { label: 'Amount refunded', value: data.totalRefundedAmount },
    breakdown: null,
    statusBadge: createStatusBadge(data.status)
  };
};

const getDefaultDisplayData = (data: EssentialInvoiceData): StatusDisplayData => {
  return {
    primaryAmount: { label: 'Invoice total', value: data.totalAmount },
    breakdown: null,
    statusBadge: createStatusBadge(data.status)
  };
};

export const hasChangesToSync = (
  invoiceCustomer: Customer,
  initialInvoiceCustomer: Customer,
  lineItems: InvoiceLineItem[] | InvoiceLineItemEditPage[],
  initialLineItems: InvoiceLineItem[] | InvoiceLineItemEditPage[],
  serviceCharge: IInvoiceServiceCharge,
  initialServiceCharge: IInvoiceServiceCharge,
  subTotal: number,
  initialSubTotal: number,
  isServiceFeeEnabled: boolean,
  isServiceFeeEnabledInitial: boolean,
  surcharge: InvoiceSurchargeStateEditPage | InvoiceSurchargeState,
  initialSurcharge: InvoiceSurchargeStateEditPage | InvoiceSurchargeState
): boolean => {
  if (!isEqual(invoiceCustomer, initialInvoiceCustomer)) {
    return true;
  }
  if (!isEqual(lineItems, initialLineItems)) {
    return true;
  }
  if (!isEqual(serviceCharge, initialServiceCharge)) {
    return true;
  }
  if (subTotal !== initialSubTotal) {
    return true;
  }
  if (!(isServiceFeeEnabled === isServiceFeeEnabledInitial && isEqual(surcharge, initialSurcharge))) {
    return true;
  }
  return false;
};

export const checkIfAnyPaymentModeIsEligible = (
  paymentModes: PaymentMode[],
  totalAmount,
  partialPaymentEnabled: boolean
) => {
  const enabledPaymentModes = paymentModes.filter(paymentMode => {
    return paymentMode.teamLevelVisiblity
      ? paymentMode.adminEnabled &&
          paymentMode.enabled &&
          (paymentMode.category.type === PaymentCategoryTypes.ASSISTED_CHECKOUT ||
            paymentMode.category.type === PaymentCategoryTypes.MANUAL)
      : paymentMode.adminEnabled;
  });

  if (enabledPaymentModes.length === 0) {
    return false;
  }

  return enabledPaymentModes.some(
    paymentMode =>
      (totalAmount >= paymentMode.minimumAmount && totalAmount <= paymentMode.maximumAmount) || partialPaymentEnabled
  );
};

export const calculateTotalOriginalUnitCost = (lineItems: InvoiceLineItem[]): number => {
  if (!lineItems?.length) return 0;

  return lineItems.reduce((total, lineItem) => {
    const quantity = parseFloat(lineItem.quantity) || 0;
    const originalUnitCost = parseFloat(lineItem.originalUnitCost) || 0;
    return total + quantity * originalUnitCost;
  }, 0);
};

export const checkIfInvoiceIsPaid = (invoiceStatusIndicator: StatusIndicator) => {
  if (!invoiceStatusIndicator) {
    return false;
  }
  return invoiceStatusIndicator === StatusIndicator.FULLY_PAID;
};

export const getPaymentMethodFilters = () => {
  const { app } = store.getState().rootReducer;
  const { configs } = app.merchantCustomizations?.teamFeatures || {};
  const { showCheckDepositInFilters } = configs || {};

  if (showCheckDepositInFilters) {
    return [...PaymentMethodsForInvoiceFilter, { key: 'check-deposit', value: 'Check deposit' }];
  }

  return PaymentMethodsForInvoiceFilter;
};

// V2 API Response Transformation Functions

export const prepareInvoiceLineItemsFromV2Response = (lineItemList: LineItemV2[]) => {
  if (!lineItemList || lineItemList.length === 0) {
    return [];
  }

  const invoiceLineItemList = lineItemList.map((lineItem, index) => {
    return {
      id: index.toString(),
      catalogueLineItemId: lineItem.catalogueLineItemId,
      itemDescription: lineItem.name,
      additionalDetails: lineItem.additionalDetails,
      quantity: lineItem.qty.toString(),
      total: lineItem.unitPrice * lineItem.qty,
      unitCost: lineItem.unitPrice.toString(),
      originalUnitCost: lineItem.unitCost?.toString(),
      markupPercentage: lineItem.markupPercentage?.toString(),
      taxes: lineItem.taxes?.map(tax => ({
        id: tax.catalogueTaxId,
        name: tax.name,
        percentage: tax.percentage.toString(),
        amount: tax.amount
      })),
      sku: lineItem.sku || ''
    };
  });

  return invoiceLineItemList;
};

export const prepareInvoiceCustomerFromV2Response = (customer: Customer, merchantId?: string) => {
  if (!customer) {
    return null;
  }

  const hasServiceAddressData =
    customer?.serviceAddress?.length > 0 &&
    Boolean(
      customer?.serviceAddress?.[0]?.id ||
        customer?.serviceAddress?.[0]?.street ||
        customer?.serviceAddress?.[0]?.apartment ||
        customer?.serviceAddress?.[0]?.city ||
        customer?.serviceAddress?.[0]?.state ||
        customer?.serviceAddress?.[0]?.country ||
        customer?.serviceAddress?.[0]?.zipCode
    );

  return {
    id: customer.id,
    ID: customer.id,
    name: customer?.name || '',
    phoneNumber: customer?.phoneNumber || '',
    email: customer?.email || '',
    merchantId: merchantId || '',
    billingAddress: customer?.billingAddress || {},
    lastName: customer?.lastName || '',
    serviceAddress: hasServiceAddressData ? customer?.serviceAddress : [],
    companyName: customer?.companyName || ''
  } as Customer;
};

export const prepareInvoiceDiscountFromV2Response = (discount: DiscountV2) => {
  if (!discount || !discount.enabled) {
    return {
      discountPercent: null,
      discountAmount: null,
      discountInCurrency: null,
      amountAfterDiscount: null,
      isDiscountAmountSelected: false
    };
  }

  return {
    discountPercent: discount.type === 'PERCENTAGE' ? discount.value.toString() : null,
    discountAmount: discount.type === 'ABSOLUTE' ? discount.amount.toString() : null,
    isDiscountAmountSelected: discount.type === 'ABSOLUTE',
    discountInCurrency: discount.amount.toString(),
    amountAfterDiscount: null
  };
};

export const prepareInvoiceServiceChargeFromV2Response = (serviceCharge: any) => {
  if (!serviceCharge) {
    return {
      id: 0,
      name: '',
      amount: '',
      percentage: '',
      totalAmount: '',
      taxes: []
    };
  }

  return {
    id: serviceCharge.id,
    name: serviceCharge.name,
    amount: serviceCharge.amount.toString(),
    percentage: serviceCharge.percentage.toString(),
    totalAmount: serviceCharge.totalAmount.toString(),
    taxes:
      serviceCharge.taxes?.map((tax: any) => ({
        id: tax.catalogueTaxId,
        name: tax.name,
        percentage: tax.percentage.toString()
      })) || []
  };
};

export const prepareInvoiceAttachments = (attachmentList: Attachment[]) => {
  if (attachmentList === null) {
    return [];
  }
  const invoiceAttachmentList: InvoiceAttachmentEditPage[] = attachmentList?.map((attachment, index) => {
    return {
      id: attachment.id ? attachment.id.toString() : index.toString(),
      url: attachment.url,
      title: attachment.title,
      imagePreview: attachment.urlLink,
      description: attachment.description,
      source: attachment.source,
      urlLink: attachment.urlLink ?? ''
    };
  });

  return invoiceAttachmentList;
};

export const recalculateReminderDates = (reminders: ReminderConfig[], newDueDate: string): ReminderConfig[] => {
  if (!reminders || reminders.length === 0 || !newDueDate) {
    return reminders;
  }

  return reminders.map(reminder => {
    const newReminderDate = calculateScheduledDate(newDueDate, reminder.scheduleRelation, reminder.days);
    return {
      ...reminder,
      reminderDate: newReminderDate
    };
  });
};

export const formatInvoiceFilterParamsForAPI = (queryParams: InvoiceV2QueryParams) => {
  const filteredParams = filterEmptyParams(queryParams);
  const formattedQueryParams: Record<string, any> = {};

  Object.entries(filteredParams).forEach(([key, value]) => {
    if (typeof value === 'string') {
      formattedQueryParams[key] = value.trim();
    } else if (Array.isArray(value)) {
      formattedQueryParams[key] = value.join(',');
    } else {
      formattedQueryParams[key] = value;
    }
  });

  return formattedQueryParams;
};

export const getMaximumAvailableAmountForTeam = (teamPaymentModes: PaymentMode[]) => {
  const enabledPaymentModes = teamPaymentModes.filter(paymentMode => {
    return paymentMode.teamLevelVisiblity ? paymentMode.adminEnabled && paymentMode.enabled : paymentMode.adminEnabled;
  });

  return enabledPaymentModes.reduce((acc, paymentMode) => {
    const maxAmount = +paymentMode.maximumAmount;
    if (maxAmount > 0) {
      return Math.max(acc, maxAmount);
    }
    return acc;
  }, 0);
};

export const checkIfInvoiceTotalExceedsPaymentModeLimit = (
  invoiceTotal: number,
  invoicePaymentModes: InvoiceLevelPaymentModes[]
) => {
  if (!invoicePaymentModes || invoicePaymentModes.length === 0) {
    return false;
  }
  return invoicePaymentModes
    .filter(paymentMode => paymentMode.isSelected && paymentMode.teamLevelVisiblity)
    .every(paymentMode => +paymentMode.maximumAmount > 0 && invoiceTotal > +paymentMode.maximumAmount);
};

export const getDisputeRefundStatusText = (statusIndicator: DISPUTE_REFUND_STATUS_INDICATOR) => {
  switch (statusIndicator) {
    case DISPUTE_REFUND_STATUS_INDICATOR.FULL:
      return 'Full';
    case DISPUTE_REFUND_STATUS_INDICATOR.PARTIAL:
      return 'Partial';
    case DISPUTE_REFUND_STATUS_INDICATOR.NONE:
      return '-';
    default:
      return '-';
  }
};

const ADDRESS_FIELDS_TO_CHECK = ['street', 'city', 'state', 'zipCode', 'country'] as const;

export const hasAddressValue = (address: AddressField | null | undefined): boolean => {
  if (!address || typeof address !== 'object') {
    return false;
  }
  return ADDRESS_FIELDS_TO_CHECK.some(
    field =>
      typeof (address as unknown as Record<string, unknown>)[field] === 'string' &&
      ((address as unknown as Record<string, unknown>)[field] as string).trim() !== ''
  );
};

export interface GetInvoiceThresholdValidationErrorsParams {
  totalAmount: number;
  threshold: string | undefined;
  customer: Customer | null;
}

export const getInvoiceThresholdValidationErrors = (
  params: GetInvoiceThresholdValidationErrorsParams
): Partial<InvoiceErrorFields> => {
  const { totalAmount, threshold, customer } = params;
  const thresholdNum = threshold ? +threshold : 0;
  if (!threshold || totalAmount <= thresholdNum) {
    return {};
  }
  if (!customer) {
    return {
      customerFirstName: true,
      customerLastName: true,
      emailOrPhone: true,
      serviceOrBillingAddress: true
    };
  }
  const errors: Partial<InvoiceErrorFields> = {};
  // const firstName = customer.name?.trim() ?? '';
  // const lastName = customer.lastName?.trim() ?? '';
  // if (firstName.length < 3 || firstName === lastName) {
  //   errors.customerFirstName = true;
  // }
  // if (lastName.length < 1) {
  //   errors.customerLastName = true;
  // }
  // const hasEmail = (customer.email?.trim() ?? '') !== '';
  // const hasPhone = (customer.phoneNumber?.trim() ?? '') !== '';
  // if (!hasEmail && !hasPhone) {
  //   errors.emailOrPhone = true;
  // }
  const serviceAddress = Array.isArray(customer.serviceAddress) ? customer.serviceAddress[0] : customer.serviceAddress;
  const hasServiceAddress = hasAddressValue(serviceAddress);
  const hasBillingAddress = hasAddressValue(customer.billingAddress);
  if (!hasServiceAddress && !hasBillingAddress) {
    errors.serviceOrBillingAddress = true;
  }
  return errors;
};
